
import React, { useState } from 'react';
import { Search, Phone, Mail, User, XCircle, AlertCircle, CheckCircle, Calendar, MapPin } from 'lucide-react';
import { Registrant, EventOption } from '../types';
import { findRegistrationsByContact, cancelEventFromRegistration } from '../services/firebaseService';
import { ToastType } from './Toast';
import { isCancelDeadlinePassed, getCancelDeadlineDate, toMonthDayLabel } from '../utils/dateUtils';

interface MyRegistrationProps {
  events: EventOption[];
  showToast: (type: ToastType, title: string, message?: string) => void;
}

const MyRegistration: React.FC<MyRegistrationProps> = ({ events, showToast }) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [searchMode, setSearchMode] = useState<'email' | 'name'>('email');
  const [name, setName] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [myRegistrations, setMyRegistrations] = useState<Registrant[]>([]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (searchMode === 'email') {
      if (!email.trim() || !phone.trim()) {
        showToast('error', '資料不完整', '請輸入電子信箱與電話號碼');
        return;
      }
    } else {
      if (!name.trim() || !phone.trim()) {
        showToast('error', '資料不完整', '請輸入姓名與電話號碼');
        return;
      }
    }

    setIsLoading(true);
    try {
      const contact = searchMode === 'email'
        ? { email: email.trim(), phone: phone.trim() }
        : { name: name.trim(), phone: phone.trim() };
      const results = await findRegistrationsByContact(contact);
      setMyRegistrations(results);
      setHasSearched(true);

      if (results.length > 0) {
        showToast('success', '查詢成功', `找到 ${results.length} 筆報名資料`);
      } else {
        showToast('info', '查無資料', '請確認輸入的資訊是否與報名時一致');
      }
    } catch (error) {
      console.error(error);
      showToast('error', '系統錯誤', '查詢時發生錯誤，請稍後再試');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async (regId: string, eventId: string, eventName: string, currentEvents: string[], email: string, phone: string) => {
    const targetEvent = events.find(ev => ev.id === eventId);
    if (targetEvent && isCancelDeadlinePassed(targetEvent)) {
      showToast('info', '已過線上取消期限', '如需異動，請聯絡醫宣幹部。');
      return;
    }
    if (!window.confirm(`確定要取消參加「${eventName}」嗎？\n取消後無法復原，需重新報名。`)) {
      return;
    }

    try {
      await cancelEventFromRegistration(regId, eventId, currentEvents, email, phone);

      // Optimistically update UI
      setMyRegistrations(prev => prev.map(reg => {
        if (reg.id === regId) {
          return {
            ...reg,
            selectedEvents: reg.selectedEvents.filter(id => id !== eventId)
          };
        }
        return reg;
      }));

      showToast('success', '取消成功', `已取消 ${eventName} 的報名`);
    } catch (error) {
      console.error(error);
      const code = (error as { code?: string })?.code || '';
      if (code.includes('failed-precondition')) {
        showToast('info', '已過線上取消期限', '如需異動，請聯絡醫宣幹部。');
      } else {
        showToast('error', '取消失敗', '系統發生錯誤，請聯繫管理員');
      }
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 animate-fade-in-up">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-extrabold text-gray-900 mb-3 flex items-center justify-center">
          <Search className="mr-3 text-blue-700" size={32} />
          查詢 / 取消報名
        </h2>
        <p className="text-gray-500">請填寫報名時使用的聯絡資訊。</p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8">
        <form onSubmit={handleSearch} className="space-y-6">
          <div className="flex rounded-xl bg-gray-100 p-1 mb-2">
            <button
              type="button"
              onClick={() => { setSearchMode('email'); setMyRegistrations([]); setHasSearched(false); }}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${searchMode === 'email' ? 'bg-white shadow text-blue-800' : 'text-gray-500'}`}
            >
              用 Email 查
            </button>
            <button
              type="button"
              onClick={() => { setSearchMode('name'); setMyRegistrations([]); setHasSearched(false); }}
              className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${searchMode === 'name' ? 'bg-white shadow text-blue-800' : 'text-gray-500'}`}
            >
              用 姓名 查
            </button>
          </div>
          {searchMode === 'email' ? (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">電子信箱</label>
              <div className="relative">
                <Mail className="absolute left-4 top-3.5 text-gray-400" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@gmail.com"
                  className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">姓名</label>
              <div className="relative">
                <User className="absolute left-4 top-3.5 text-gray-400" size={20} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="請輸入姓名"
                  className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">聯絡電話</label>
            <div className="relative">
              <Phone className="absolute left-4 top-3.5 text-gray-400" size={20} />
              <input
                type="text"
                value={phone}
                onChange={(e) => {
                  const value = e.target.value;
                  const digits = value.replace(/\D/g, '');
                  let formatted = digits;
                  if (digits.length > 4) {
                    formatted = `${digits.slice(0, 4)}-${digits.slice(4, 10)}`;
                  }
                  setPhone(formatted);
                }}
                placeholder="09xxxxxxxx"
                maxLength={11}
                className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-800 text-white font-bold py-3 rounded-xl hover:bg-blue-900 shadow-md active:scale-95 transition-all flex justify-center items-center"
          >
            {isLoading ? '查詢中...' : '查詢我的報名'}
          </button>
        </form>
      </div>

      {/* Results Area */}
      {hasSearched && (
        <div className="animate-fade-in">
          {myRegistrations.length > 0 ? (
            <div className="space-y-6">
              <div className="flex items-center mb-4 text-gray-700 font-bold">
                <CheckCircle className="text-green-500 mr-2" size={20} />
                <span>查詢結果：{myRegistrations[0].name} ({myRegistrations[0].church})</span>
              </div>

              {myRegistrations.map((reg) => {
                // Get full event objects for this registration
                const userEvents = events.filter(e => reg.selectedEvents.includes(e.id));

                if (userEvents.length === 0) return null;

                return (
                  <div key={reg.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-6 py-3 border-b border-gray-100 flex justify-between items-center">
                      <span className="text-xs font-bold text-gray-400 uppercase">報名時間：{new Date(reg.submissionDate).toLocaleDateString()}</span>
                      <div className="flex flex-wrap gap-1">
                        {(reg.roles && reg.roles.length > 0 ? reg.roles : [reg.role]).map((role, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded font-bold">
                            {role.split('~')[0]}
                            {role === '學生' && reg.studentSpecialization && (
                              <span className="ml-1">({reg.studentSpecialization})</span>
                            )}
                            {role === '西醫' && reg.westernMedicineSpecialty && (
                              <span className="ml-1">({reg.westernMedicineSpecialty})</span>
                            )}
                            {role === '治療師' && reg.therapistSpecialty && (
                              <span className="ml-1">({reg.therapistSpecialty})</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {userEvents.map(evt => (
                        <div key={evt.id} className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center hover:bg-gray-50 transition-colors group">
                          <div className="mb-4 sm:mb-0">
                            <div className="text-lg font-bold text-gray-800 flex items-center">
                              <Calendar size={18} className="mr-2 text-blue-500" />
                              {evt.dateRange}
                            </div>
                            <div className="text-sm text-gray-500 mt-1 flex items-center">
                              <MapPin size={14} className="mr-1.5" />
                              {evt.location}
                            </div>
                            {(() => {
                              const a = reg.assignments?.[evt.id];
                              const lead = a?.leadershipRole && a.leadershipRole !== '無' ? a.leadershipRole : '';
                              const task = a?.taskRole && a.taskRole !== '尚未分配' ? a.taskRole : '';
                              const sub = a?.taskSubDetail || '';
                              const work = [lead, task, sub].filter(Boolean).join(' / ');
                              return work ? (
                                <div className="text-sm text-indigo-700 font-bold mt-2 flex items-center">
                                  <CheckCircle size={14} className="mr-1.5" />
                                  工作：{work}
                                </div>
                              ) : null;
                            })()}
                          </div>
                          {searchMode === 'email' && isCancelDeadlinePassed(evt) && (
                            <div className="text-sm text-gray-500 leading-relaxed sm:text-right sm:max-w-[240px]">
                              線上取消已於 {(() => { const c = getCancelDeadlineDate(evt); return c ? toMonthDayLabel(c) : ''; })()} 截止，<br className="hidden sm:inline" />如需異動，請聯絡醫宣幹部。
                            </div>
                          )}
                          {searchMode === 'email' && !isCancelDeadlinePassed(evt) && (
                            <button
                              onClick={() => handleCancel(reg.id, evt.id, evt.dateRange, reg.selectedEvents, reg.email, reg.phone)}
                              className="flex items-center px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-300 transition-all text-sm font-bold btn-press"
                            >
                              <XCircle size={16} className="mr-2" />
                              取消報名
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Show message if user exists but all events canceled */}
              {myRegistrations.every(r => r.selectedEvents.length === 0) && (
                <div className="bg-gray-100 p-8 rounded-2xl text-center text-gray-500 italic">
                  您目前沒有參加任何梯隊。
                </div>
              )}

            </div>
          ) : (
            <div className="bg-gray-50 border-2 border-dashed border-gray-200 p-10 rounded-2xl text-center">
              <AlertCircle className="mx-auto text-gray-300 mb-3" size={48} />
              <h3 className="text-lg font-bold text-gray-600">查無報名資料</h3>
              <p className="text-gray-400 text-sm mt-1">
                請確認姓名或電子信箱及電話是否與報名時一致。
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyRegistration;
